import { useMutation } from '@tanstack/react-query';
import { useBigIntVisitorIdCookie } from 'common-hooks';
import { HttpError } from 'common-utils';
import { DocumentId, TUploadErrorCode } from 'preflight-core';

import { Journal } from '../resources/journal';
import { uploadFile, UploadPayload } from '../resources/upload-file';
import { useUploadRedirect } from '../resources/use-upload-redirect';
import { useUTMParameters } from './utm-parameters';

export type UploadState =
  | { name: 'idle' }
  | { name: 'loading' }
  | { name: 'success'; data: UploadPayload }
  | { name: 'error'; error: HttpError };

type useUploadDocumentType = {
  demoId?: DocumentId;
  journalId: Journal['slug'];
  publisherId: Journal['publisher']['slug'];
  parentDocumentId?: DocumentId;
  reUpload?: boolean;
  editorialDesk?: boolean;
  isContinueWithPdf?: boolean;
  preflightNudgeAccepted?: boolean;
  articleTypeId?: number;
  selectedAddonId?: string;
  isAddonEnabledV3?: boolean;
  availableModules?: string[];
};

type useUploadDocumentProperties = {
  resetUpload: () => void;
  uploadState: UploadState;
  upload: (
    file: File,
    preflightNudgeAccepted?: boolean,
    manuscriptId?: string,
  ) => void;
  tryDemo?: () => void;
  redirectToD: (
    data: UploadPayload,
    isContinueWithPdf?: boolean,
    setUploadStateContinuePdf?: (data: {
      name: string;
      error?: { json: { errorCode: TUploadErrorCode; errorReason: string } };
      data?: { json: object };
    }) => void,
  ) => void;
};

export function useUploadDocument({
  demoId,
  journalId,
  publisherId,
  parentDocumentId,
  reUpload,
  articleTypeId,
  articleTypeStageId,
  editorialDesk = false,
  selectedAddonId,
  isAddonEnabledV3 = false,
  availableModules,
}: useUploadDocumentType & {
  articleTypeStageId?: number;
}): useUploadDocumentProperties {
  const { redirectToDocumentPage } = useUploadRedirect({
    publisherId,
    journalId,
    editorialDesk,
    selectedAddonId,
    isAddonEnabledV3,
  });

  const bigIntVisitorId = useBigIntVisitorIdCookie();
  const uploadFileMutation = useMutation<
    UploadPayload,
    HttpError,
    Parameters<typeof uploadFile>[0]
  >({
    mutationFn: uploadFile,
    retry: (failureCount, error) => {
      return error?.message?.includes('Failed to fetch') && failureCount < 2;
    },
    retryDelay: 1500,
  });

  const { utm_source, utm_medium, utm_campaign, referrer } = useUTMParameters();

  return {
    resetUpload: () => {
      uploadFileMutation.reset();
    },
    uploadState:
      uploadFileMutation.status === 'loading'
        ? { name: 'loading' }
        : uploadFileMutation.status === 'error'
        ? { name: 'error', error: uploadFileMutation.error }
        : uploadFileMutation.status === 'success'
        ? { name: 'success', data: uploadFileMutation.data }
        : { name: 'idle' },
    upload: (file, preflightNudgeAccepted, manuscriptId) => {
      uploadFileMutation.mutate({
        bigIntVisitorId,
        file,
        journalId,
        publisherId,
        parentDocumentId,
        reUpload,
        editorialDesk,
        utm_source,
        utm_medium,
        utm_campaign,
        referrer,
        preflightNudgeAccepted,
        articleTypeId,
        articleTypeStageId,
        availableModules,
        manuscriptId,
      });
    },

    redirectToD: (data, isContinueWithPdf, setUploadStateContinuePdf) => {
      redirectToDocumentPage(
        data,
        isContinueWithPdf,
        setUploadStateContinuePdf,
      );
    },
    tryDemo: demoId
      ? () => {
          redirectToDocumentPage({
            documentId: demoId,
            hasExistingTrackChanges: false,
            hasMacros: false,
          });
        }
      : undefined,
  };
}
